import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Storefront, Receipt, ChatCircleDots, EnvelopeSimple, Globe, MagnifyingGlass,
  ShieldCheck, Gear, ArrowLeft, FloppyDisk, CheckCircle, WhatsappLogo, Key, EyeSlash,
} from "@phosphor-icons/react";

// ==================================================================
// Shared wrapper — page shell with breadcrumb, save state, and form area
// ==================================================================
export function SettingsShell({ ns, title, subtitle, icon: Ic, children, form, defaults, testid, canSave = true, onLoaded }) {
  const [initial, setInitial] = useState(defaults);
  const [data, setData]       = useState(defaults);
  const [meta, setMeta]       = useState({ updated_at: "", updated_by: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/settings/${ns}`);
      const merged = { ...defaults, ...(d.data || {}) };
      setInitial(merged); setData(merged);
      setMeta({ updated_at: d.updated_at, updated_by: d.updated_by });
      onLoaded?.(merged);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  }, [ns, defaults, onLoaded]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const { data: d } = await api.put(`/settings/${ns}`, { data });
      setInitial(data);
      setMeta({ updated_at: d.updated_at, updated_by: d.updated_by });
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const dirty = JSON.stringify(data) !== JSON.stringify(initial);
  const set   = (k, v) => setData((d) => ({ ...d, [k]: v }));

  return (
    <div className="max-w-4xl mx-auto p-4 lg:p-6 space-y-5" data-testid={testid}>
      <Link to="/settings" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#1e3a8a]" data-testid="settings-back">
        <ArrowLeft size={12} /> All Settings
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
            <Ic size={22} weight="duotone" className="text-[#1e3a8a]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{title}</h1>
            <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
            {meta.updated_at && (
              <div className="text-[11px] text-slate-400 mt-1">
                Last saved {new Date(meta.updated_at).toLocaleString("en-IN")} · by {meta.updated_by}
              </div>
            )}
          </div>
        </div>
        {canSave && (
          <Button onClick={save} disabled={!dirty || saving} data-testid="settings-save" className="bg-orange-600 hover:bg-orange-700 text-white gap-2 h-10 px-5">
            <FloppyDisk size={14} weight="fill" /> {saving ? "Saving…" : dirty ? "Save Changes" : "Saved"}
            {!dirty && !saving && <CheckCircle size={12} weight="fill" />}
          </Button>
        )}
      </div>

      <Card className="p-6 bg-white border-slate-200 shadow-sm">
        {loading ? (
          <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
        ) : (form ? form({ data, set, dirty }) : children)}
      </Card>
    </div>
  );
}

// Utility form field wrapper
export function Field({ label, hint, children, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      {children}
      {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
    </div>
  );
}
export function SectionTitle({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500 pb-2 border-b border-slate-100">{children}</div>;
}

// ==================================================================
// 1) Business Profile
// ==================================================================
const BUSINESS_DEFAULT = {
  legal_name: "Priyanka Enterprises",
  brand_name: "DigitalSetu",
  tagline: "Connecting Citizens to Government Services",
  gstin: "",
  pan: "",
  cin: "",
  address_line1: "Shop No. 4, Ground Floor, Yakub Manzil",
  address_line2: "Road No. 27, Shanti Nagar, Wagle Estate",
  city: "Thane",
  state: "Maharashtra",
  pincode: "400604",
  country: "India",
  phone: "+91 81694 82911",
  whatsapp: "+91 81694 82911",
  email: "hello@digitalsetu.in",
  website: "https://digitalsetu.in",
  invoice_prefix: "PE",
  currency: "INR",
  fiscal_year_start: "04-01",
  logo_url: "",
};

export function BusinessProfile() {
  return (
    <SettingsShell ns="business" title="Business Profile" subtitle="Legal identity, tax numbers, and invoice defaults." icon={Storefront} defaults={BUSINESS_DEFAULT} testid="settings-business"
      form={({ data, set }) => (
        <div className="space-y-6">
          <div>
            <SectionTitle>Identity</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              <Field label="Legal Name"><Input value={data.legal_name} onChange={(e) => set("legal_name", e.target.value)} data-testid="biz-legal" /></Field>
              <Field label="Brand Name"><Input value={data.brand_name} onChange={(e) => set("brand_name", e.target.value)} data-testid="biz-brand" /></Field>
              <Field label="Tagline" hint="Shown on invoices and payslips">
                <Input value={data.tagline} onChange={(e) => set("tagline", e.target.value)} />
              </Field>
              <Field label="Logo URL" hint="https link to your logo (used on invoices)">
                <Input value={data.logo_url} onChange={(e) => set("logo_url", e.target.value)} />
              </Field>
            </div>
          </div>

          <div>
            <SectionTitle>Statutory Numbers</SectionTitle>
            <div className="grid sm:grid-cols-3 gap-4 pt-4">
              <Field label="GSTIN"><Input value={data.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} data-testid="biz-gstin" /></Field>
              <Field label="PAN"><Input value={data.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} /></Field>
              <Field label="CIN (if applicable)"><Input value={data.cin} onChange={(e) => set("cin", e.target.value.toUpperCase())} /></Field>
            </div>
          </div>

          <div>
            <SectionTitle>Address</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              <Field label="Address Line 1"><Input value={data.address_line1} onChange={(e) => set("address_line1", e.target.value)} /></Field>
              <Field label="Address Line 2"><Input value={data.address_line2} onChange={(e) => set("address_line2", e.target.value)} /></Field>
              <Field label="City"><Input value={data.city} onChange={(e) => set("city", e.target.value)} /></Field>
              <Field label="State"><Input value={data.state} onChange={(e) => set("state", e.target.value)} /></Field>
              <Field label="PIN Code"><Input value={data.pincode} onChange={(e) => set("pincode", e.target.value)} /></Field>
              <Field label="Country"><Input value={data.country} onChange={(e) => set("country", e.target.value)} /></Field>
            </div>
          </div>

          <div>
            <SectionTitle>Contact</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              <Field label="Phone"><Input value={data.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
              <Field label="WhatsApp"><Input value={data.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
              <Field label="Email"><Input value={data.email} onChange={(e) => set("email", e.target.value)} /></Field>
              <Field label="Website"><Input value={data.website} onChange={(e) => set("website", e.target.value)} /></Field>
            </div>
          </div>

          <div>
            <SectionTitle>Invoicing</SectionTitle>
            <div className="grid sm:grid-cols-3 gap-4 pt-4">
              <Field label="Invoice Prefix" hint="e.g. PE — used as PE-2026-0001">
                <Input value={data.invoice_prefix} onChange={(e) => set("invoice_prefix", e.target.value.toUpperCase())} />
              </Field>
              <Field label="Currency"><Input value={data.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} maxLength={3} /></Field>
              <Field label="Fiscal Year Start" hint="MM-DD (India uses 04-01)">
                <Input value={data.fiscal_year_start} onChange={(e) => set("fiscal_year_start", e.target.value)} placeholder="04-01" />
              </Field>
            </div>
          </div>
        </div>
      )} />
  );
}

// ==================================================================
// 2) Taxes & GST
// ==================================================================
const TAXES_DEFAULT = {
  gst_enabled: true,
  default_gst_percent: 18,
  gst_composition: false,
  place_of_supply: "Maharashtra",
  price_inclusive_gst: false,
  hsn_default: "9983",
  tds_enabled: false,
  tds_percent: 10,
  cess_percent: 0,
  gst_slabs: "0, 5, 12, 18, 28",
};
export function TaxesSettings() {
  return (
    <SettingsShell ns="taxes" title="Taxes & GST" subtitle="Default GST slab, place-of-supply, TDS, and cess rules." icon={Receipt} defaults={TAXES_DEFAULT} testid="settings-taxes"
      form={({ data, set }) => (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="GST Enabled">
            <Select value={String(data.gst_enabled)} onValueChange={(v) => set("gst_enabled", v === "true")}>
              <SelectTrigger className="h-10" data-testid="tax-enabled"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Default GST %"><Input type="number" value={data.default_gst_percent} onChange={(e) => set("default_gst_percent", Number(e.target.value))} /></Field>
          <Field label="Composition Scheme">
            <Select value={String(data.gst_composition)} onValueChange={(v) => set("gst_composition", v === "true")}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Place of Supply"><Input value={data.place_of_supply} onChange={(e) => set("place_of_supply", e.target.value)} /></Field>
          <Field label="Prices Inclusive of GST">
            <Select value={String(data.price_inclusive_gst)} onValueChange={(v) => set("price_inclusive_gst", v === "true")}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="false">No (add GST on top)</SelectItem><SelectItem value="true">Yes (extract from price)</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Default HSN/SAC" hint="Applied to services without a specific HSN"><Input value={data.hsn_default} onChange={(e) => set("hsn_default", e.target.value)} /></Field>
          <Field label="TDS Enabled">
            <Select value={String(data.tds_enabled)} onValueChange={(v) => set("tds_enabled", v === "true")}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Default TDS %"><Input type="number" value={data.tds_percent} onChange={(e) => set("tds_percent", Number(e.target.value))} /></Field>
          <Field label="Cess %"><Input type="number" value={data.cess_percent} onChange={(e) => set("cess_percent", Number(e.target.value))} /></Field>
          <Field label="Available GST Slabs" hint="Comma-separated list of % rates shown in dropdowns">
            <Input value={data.gst_slabs} onChange={(e) => set("gst_slabs", e.target.value)} />
          </Field>
        </div>
      )} />
  );
}

// ==================================================================
// 3) SMS
// ==================================================================
const SMS_DEFAULT = {
  provider: "msg91", sender_id: "DIGSTU", api_key: "", template_id_bill: "", template_id_ready: "", enabled: false,
  from_number: "", route: "transactional",
};
export function SmsSettings() {
  return (
    <SettingsShell ns="sms" title="SMS Provider" subtitle="Configure MSG91, Twilio, Fast2SMS or any transactional SMS provider." icon={ChatCircleDots} defaults={SMS_DEFAULT} testid="settings-sms"
      form={({ data, set }) => (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Enable SMS">
            <Select value={String(data.enabled)} onValueChange={(v) => set("enabled", v === "true")}>
              <SelectTrigger className="h-10" data-testid="sms-enabled"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="true">Enabled</SelectItem><SelectItem value="false">Disabled</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Provider">
            <Select value={data.provider} onValueChange={(v) => set("provider", v)}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="msg91">MSG91</SelectItem>
                <SelectItem value="twilio">Twilio</SelectItem>
                <SelectItem value="fast2sms">Fast2SMS</SelectItem>
                <SelectItem value="textlocal">TextLocal</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sender ID" hint="6-character DLT-registered sender ID"><Input value={data.sender_id} onChange={(e) => set("sender_id", e.target.value.toUpperCase())} maxLength={6} /></Field>
          <Field label="Route">
            <Select value={data.route} onValueChange={(v) => set("route", v)}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="transactional">Transactional</SelectItem><SelectItem value="promotional">Promotional</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="API Key" hint="Kept encrypted at rest">
            <div className="relative">
              <Input type="password" value={data.api_key} onChange={(e) => set("api_key", e.target.value)} placeholder="••••••••••••" />
              <Key size={12} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>
          </Field>
          <Field label="From Number (Twilio only)"><Input value={data.from_number} onChange={(e) => set("from_number", e.target.value)} placeholder="+1XXXXXXXXXX" /></Field>
          <Field label="Bill Template ID"><Input value={data.template_id_bill} onChange={(e) => set("template_id_bill", e.target.value)} /></Field>
          <Field label="Ready Template ID"><Input value={data.template_id_ready} onChange={(e) => set("template_id_ready", e.target.value)} /></Field>
        </div>
      )} />
  );
}

// ==================================================================
// 4) Email
// ==================================================================
const EMAIL_DEFAULT = {
  provider: "smtp", enabled: false, from_email: "hello@digitalsetu.in", from_name: "DigitalSetu",
  reply_to: "", smtp_host: "smtp.gmail.com", smtp_port: 587, smtp_user: "", smtp_password: "", api_key: "",
  bill_subject: "Your DigitalSetu invoice",
};
export function EmailSettings() {
  return (
    <SettingsShell ns="email" title="Email Provider" subtitle="SendGrid / Resend / SMTP settings for invoices and notifications." icon={EnvelopeSimple} defaults={EMAIL_DEFAULT} testid="settings-email"
      form={({ data, set }) => (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Enable Email">
              <Select value={String(data.enabled)} onValueChange={(v) => set("enabled", v === "true")}>
                <SelectTrigger className="h-10" data-testid="email-enabled"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="true">Enabled</SelectItem><SelectItem value="false">Disabled</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Provider">
              <Select value={data.provider} onValueChange={(v) => set("provider", v)}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="smtp">SMTP (Gmail, Zoho, etc.)</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                  <SelectItem value="resend">Resend</SelectItem>
                  <SelectItem value="ses">Amazon SES</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="From Email"><Input value={data.from_email} onChange={(e) => set("from_email", e.target.value)} /></Field>
            <Field label="From Name"><Input value={data.from_name} onChange={(e) => set("from_name", e.target.value)} /></Field>
            <Field label="Reply-To"><Input value={data.reply_to} onChange={(e) => set("reply_to", e.target.value)} /></Field>
            <Field label="Default Bill Subject"><Input value={data.bill_subject} onChange={(e) => set("bill_subject", e.target.value)} /></Field>
          </div>
          {data.provider === "smtp" ? (
            <div>
              <SectionTitle>SMTP</SectionTitle>
              <div className="grid sm:grid-cols-2 gap-4 pt-4">
                <Field label="SMTP Host"><Input value={data.smtp_host} onChange={(e) => set("smtp_host", e.target.value)} /></Field>
                <Field label="SMTP Port"><Input type="number" value={data.smtp_port} onChange={(e) => set("smtp_port", Number(e.target.value))} /></Field>
                <Field label="SMTP User"><Input value={data.smtp_user} onChange={(e) => set("smtp_user", e.target.value)} /></Field>
                <Field label="SMTP Password"><Input type="password" value={data.smtp_password} onChange={(e) => set("smtp_password", e.target.value)} placeholder="••••••••" /></Field>
              </div>
            </div>
          ) : (
            <div>
              <SectionTitle>API</SectionTitle>
              <div className="grid sm:grid-cols-2 gap-4 pt-4">
                <Field label="API Key"><Input type="password" value={data.api_key} onChange={(e) => set("api_key", e.target.value)} placeholder="••••••••••••" /></Field>
              </div>
            </div>
          )}
        </div>
      )} />
  );
}

// ==================================================================
// 5) Website Settings
// ==================================================================
const WEBSITE_DEFAULT = {
  hero_title: "Every government service. One trusted platform.",
  hero_subtitle: "PAN · Aadhaar · Passport · GST · UDYAM · 100+ services — apply online, track application, get it done.",
  cta_primary: "Apply Online",
  cta_secondary: "Track My Application",
  show_reviews: true,
  show_stats: true,
  show_wa_float: true,
  featured_services: "pan-card, aadhaar, passport, gst-registration, rent-agreement, udyam",
  announcement: "",
  footer_note: "Made in India · A product of Priyanka Enterprises",
};
export function WebsiteSettings() {
  return (
    <SettingsShell ns="website" title="Website Settings" subtitle="Homepage copy, featured services, announcement banner." icon={Globe} defaults={WEBSITE_DEFAULT} testid="settings-website"
      form={({ data, set }) => (
        <div className="space-y-6">
          <div>
            <SectionTitle>Hero</SectionTitle>
            <div className="grid gap-4 pt-4">
              <Field label="Hero Title"><Input value={data.hero_title} onChange={(e) => set("hero_title", e.target.value)} data-testid="web-hero" /></Field>
              <Field label="Hero Subtitle"><Textarea rows={2} value={data.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} className="resize-none" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Primary CTA Label"><Input value={data.cta_primary} onChange={(e) => set("cta_primary", e.target.value)} /></Field>
                <Field label="Secondary CTA Label"><Input value={data.cta_secondary} onChange={(e) => set("cta_secondary", e.target.value)} /></Field>
              </div>
            </div>
          </div>
          <div>
            <SectionTitle>Sections</SectionTitle>
            <div className="grid sm:grid-cols-3 gap-4 pt-4">
              <Field label="Show Reviews">
                <Select value={String(data.show_reviews)} onValueChange={(v) => set("show_reviews", v === "true")}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Show Stats">
                <Select value={String(data.show_stats)} onValueChange={(v) => set("show_stats", v === "true")}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Floating WhatsApp Button">
                <Select value={String(data.show_wa_float)} onValueChange={(v) => set("show_wa_float", v === "true")}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
          </div>
          <div>
            <SectionTitle>Content</SectionTitle>
            <div className="grid gap-4 pt-4">
              <Field label="Featured Services" hint="Comma-separated slugs shown on the homepage grid">
                <Input value={data.featured_services} onChange={(e) => set("featured_services", e.target.value)} />
              </Field>
              <Field label="Site-wide Announcement Banner" hint="Leave empty to hide the banner">
                <Input value={data.announcement} onChange={(e) => set("announcement", e.target.value)} placeholder="🎉 Republic Day offer — 20% off on Rent Agreements" />
              </Field>
              <Field label="Footer Note"><Input value={data.footer_note} onChange={(e) => set("footer_note", e.target.value)} /></Field>
            </div>
          </div>
        </div>
      )} />
  );
}

// ==================================================================
// 6) SEO
// ==================================================================
const SEO_DEFAULT = {
  meta_title: "DigitalSetu — Government Services & CSC Platform",
  meta_description: "PAN, Aadhaar, Passport, GST, Rent Agreement and 100+ services online + at your nearest branch.",
  meta_keywords: "PAN card, Aadhaar update, GST registration, Rent agreement, passport, CSC Thane, DigitalSetu",
  og_image: "",
  google_analytics_id: "",
  google_search_console: "",
  facebook_pixel_id: "",
  robots: "index, follow",
  sitemap_enabled: true,
  schema_business_type: "LocalBusiness",
};
export function SeoSettings() {
  return (
    <SettingsShell ns="seo" title="SEO" subtitle="Meta tags, Google Analytics, Facebook Pixel, schema, sitemap." icon={MagnifyingGlass} defaults={SEO_DEFAULT} testid="settings-seo"
      form={({ data, set }) => (
        <div className="space-y-6">
          <div>
            <SectionTitle>Meta tags</SectionTitle>
            <div className="grid gap-4 pt-4">
              <Field label="Meta Title" hint="~60 chars — shown in browser tab and Google results">
                <Input value={data.meta_title} onChange={(e) => set("meta_title", e.target.value)} maxLength={80} />
              </Field>
              <Field label="Meta Description" hint="~155 chars — Google search snippet">
                <Textarea rows={3} value={data.meta_description} onChange={(e) => set("meta_description", e.target.value)} className="resize-none" maxLength={200} />
              </Field>
              <Field label="Meta Keywords" hint="Comma-separated"><Input value={data.meta_keywords} onChange={(e) => set("meta_keywords", e.target.value)} /></Field>
              <Field label="OG Image URL" hint="Shown when your site is shared on WhatsApp, Twitter, LinkedIn">
                <Input value={data.og_image} onChange={(e) => set("og_image", e.target.value)} placeholder="https://digitalsetu.in/og.png" />
              </Field>
            </div>
          </div>
          <div>
            <SectionTitle>Analytics & Ads</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              <Field label="Google Analytics ID"><Input value={data.google_analytics_id} onChange={(e) => set("google_analytics_id", e.target.value)} placeholder="G-XXXXXXX" /></Field>
              <Field label="Google Search Console"><Input value={data.google_search_console} onChange={(e) => set("google_search_console", e.target.value)} placeholder="verification token" /></Field>
              <Field label="Facebook Pixel ID"><Input value={data.facebook_pixel_id} onChange={(e) => set("facebook_pixel_id", e.target.value)} /></Field>
              <Field label="Robots"><Input value={data.robots} onChange={(e) => set("robots", e.target.value)} /></Field>
            </div>
          </div>
          <div>
            <SectionTitle>Sitemap & Schema</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              <Field label="Sitemap Enabled">
                <Select value={String(data.sitemap_enabled)} onValueChange={(v) => set("sitemap_enabled", v === "true")}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Business Schema Type">
                <Select value={data.schema_business_type} onValueChange={(v) => set("schema_business_type", v)}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="LocalBusiness">LocalBusiness</SelectItem>
                    <SelectItem value="Organization">Organization</SelectItem>
                    <SelectItem value="ProfessionalService">ProfessionalService</SelectItem>
                    <SelectItem value="GovernmentService">GovernmentService</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>
        </div>
      )} />
  );
}

// ==================================================================
// 7) Security
// ==================================================================
const SECURITY_DEFAULT = {
  password_min_length: 8,
  require_uppercase: true,
  require_number: true,
  require_symbol: false,
  password_expiry_days: 0,
  two_factor_enabled: false,
  session_timeout_mins: 60,
  max_login_attempts: 5,
  lockout_minutes: 15,
  ip_allowlist: "",
  audit_retention_days: 365,
  data_encryption_notice: "Customer PII is encrypted with AES-256-GCM at rest.",
};
function PinLoginGlobalToggle() {
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/auth/pin/global").then((r) => setEnabled(!!r.data.enabled)).catch(() => {});
  }, []);
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/pin/global", { enabled: !enabled });
      setEnabled(!!data.enabled);
      toast.success(data.enabled ? "Quick PIN login enabled for all users" : "Quick PIN login disabled for all users");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="pin-global-toggle-card">
      <div>
        <div className="text-sm font-semibold text-slate-900">Quick PIN Login</div>
        <div className="text-xs text-slate-500 mt-0.5">Device-bound 4/6-digit PIN sign-in. Super Admin can turn it off for all users.</div>
      </div>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid="pin-global-toggle"
        className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border transition-colors ${
          enabled ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300"
        }`}
      >
        {enabled ? "Enabled" : "Disabled"}
      </button>
    </div>
  );
}

export function SecuritySettings() {
  return (
    <SettingsShell ns="security" title="Security" subtitle="Password policy, session timeout, 2FA, IP allow-list, audit retention." icon={ShieldCheck} defaults={SECURITY_DEFAULT} testid="settings-security"
      form={({ data, set }) => (
        <div className="space-y-6">
          <PinLoginGlobalToggle />
          <div>
            <SectionTitle>Password Policy</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              <Field label="Minimum Length"><Input type="number" min={6} max={64} value={data.password_min_length} onChange={(e) => set("password_min_length", Number(e.target.value))} data-testid="sec-min-len" /></Field>
              <Field label="Password Expiry (days)" hint="0 = never expires"><Input type="number" min={0} value={data.password_expiry_days} onChange={(e) => set("password_expiry_days", Number(e.target.value))} /></Field>
              <Field label="Require Uppercase">
                <Select value={String(data.require_uppercase)} onValueChange={(v) => set("require_uppercase", v === "true")}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Require Number">
                <Select value={String(data.require_number)} onValueChange={(v) => set("require_number", v === "true")}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Require Symbol">
                <Select value={String(data.require_symbol)} onValueChange={(v) => set("require_symbol", v === "true")}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
          </div>
          <div>
            <SectionTitle>Session &amp; Brute Force</SectionTitle>
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              <Field label="Session Timeout (mins)"><Input type="number" min={5} value={data.session_timeout_mins} onChange={(e) => set("session_timeout_mins", Number(e.target.value))} /></Field>
              <Field label="Max Login Attempts"><Input type="number" min={1} value={data.max_login_attempts} onChange={(e) => set("max_login_attempts", Number(e.target.value))} /></Field>
              <Field label="Lockout Duration (mins)"><Input type="number" min={1} value={data.lockout_minutes} onChange={(e) => set("lockout_minutes", Number(e.target.value))} /></Field>
              <Field label="Two-Factor Authentication">
                <Select value={String(data.two_factor_enabled)} onValueChange={(v) => set("two_factor_enabled", v === "true")}>
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="true">Enabled</SelectItem><SelectItem value="false">Disabled</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
          </div>
          <div>
            <SectionTitle>Access &amp; Audit</SectionTitle>
            <div className="grid gap-4 pt-4">
              <Field label="IP Allow-List" hint="Comma-separated CIDR / IP addresses. Leave empty to allow all.">
                <Input value={data.ip_allowlist} onChange={(e) => set("ip_allowlist", e.target.value)} placeholder="103.21.244.0/22, 192.168.1.5" />
              </Field>
              <Field label="Audit Log Retention (days)"><Input type="number" min={30} value={data.audit_retention_days} onChange={(e) => set("audit_retention_days", Number(e.target.value))} /></Field>
              <Field label="Data Encryption Notice" hint="Displayed on the Contact + About pages">
                <Textarea rows={2} value={data.data_encryption_notice} onChange={(e) => set("data_encryption_notice", e.target.value)} className="resize-none" />
              </Field>
            </div>
          </div>
        </div>
      )} />
  );
}

// ==================================================================
// 8) General
// ==================================================================
const GENERAL_DEFAULT = {
  language: "en-IN",
  timezone: "Asia/Kolkata",
  date_format: "DD-MM-YYYY",
  time_format: "12h",
  first_day_of_week: "Monday",
  default_country_code: "+91",
  enable_dark_mode: false,
  low_stock_alerts: true,
};
export function GeneralSettings() {
  return (
    <SettingsShell ns="general" title="General Settings" subtitle="Language, timezone, date/time format, and defaults." icon={Gear} defaults={GENERAL_DEFAULT} testid="settings-general"
      form={({ data, set }) => (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Language">
            <Select value={data.language} onValueChange={(v) => set("language", v)}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="en-IN">English (India)</SelectItem>
                <SelectItem value="hi-IN">हिंदी (Hindi)</SelectItem>
                <SelectItem value="mr-IN">मराठी (Marathi)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Timezone"><Input value={data.timezone} onChange={(e) => set("timezone", e.target.value)} /></Field>
          <Field label="Date Format">
            <Select value={data.date_format} onValueChange={(v) => set("date_format", v)}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="DD-MM-YYYY">DD-MM-YYYY</SelectItem>
                <SelectItem value="MM-DD-YYYY">MM-DD-YYYY</SelectItem>
                <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Time Format">
            <Select value={data.time_format} onValueChange={(v) => set("time_format", v)}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="12h">12-hour</SelectItem><SelectItem value="24h">24-hour</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="First Day of Week">
            <Select value={data.first_day_of_week} onValueChange={(v) => set("first_day_of_week", v)}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="Monday">Monday</SelectItem><SelectItem value="Sunday">Sunday</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Default Country Code"><Input value={data.default_country_code} onChange={(e) => set("default_country_code", e.target.value)} /></Field>
          <Field label="Enable Dark Mode">
            <Select value={String(data.enable_dark_mode)} onValueChange={(v) => set("enable_dark_mode", v === "true")}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Low-stock Alerts">
            <Select value={String(data.low_stock_alerts)} onValueChange={(v) => set("low_stock_alerts", v === "true")}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
            </Select>
          </Field>
        </div>
      )} />
  );
}
